import { Link } from "@tanstack/react-router";
import { MapPin } from "lucide-react";

type Props = {
  /** Service slug — normalizes to "seo" or "web-design" city route. Defaults to "web-design". */
  serviceSlug?: string;
  /** Optional context word for subcopy, e.g. "SEO", "law firms". */
  contextLabel?: string;
};

function normalizeServiceSlug(slug?: string): "seo" | "web-design" {
  if (!slug) return "web-design";
  if (slug === "seo" || slug === "local-seo") return "seo";
  return "web-design";
}

const MAJOR_METROS: { name: string; slug: string }[] = [
  { name: "Denver", slug: "denver" },
  { name: "Colorado Springs", slug: "colorado-springs" },
  { name: "Boulder", slug: "boulder" },
  { name: "Fort Collins", slug: "fort-collins" },
  { name: "Aurora", slug: "aurora" },
  { name: "Lakewood", slug: "lakewood" },
  { name: "Loveland", slug: "loveland" },
  { name: "Pueblo", slug: "pueblo" },
];

const GROWTH_MARKETS: { name: string; slug: string }[] = [
  { name: "Castle Rock", slug: "castle-rock" },
  { name: "Parker", slug: "parker" },
  { name: "Littleton", slug: "littleton" },
  { name: "Longmont", slug: "longmont" },
  { name: "Greeley", slug: "greeley" },
  { name: "Westminster", slug: "westminster" },
  { name: "Broomfield", slug: "broomfield" },
  { name: "Centennial", slug: "centennial" },
];

function CityChips({
  label,
  items,
  serviceSlug,
}: {
  label: string;
  items: { name: string; slug: string }[];
  serviceSlug: "seo" | "web-design";
}) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
        {label}
      </p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {items.map((c) => (
          <li key={c.slug}>
            <Link
              to={`/${c.slug}-${serviceSlug}` as string}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground/85 shadow-card transition-colors hover:border-brand-gold-deep hover:text-brand-gold-deep"
            >
              <MapPin className="h-3 w-3 text-brand-gold-deep" />
              {c.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StatewideCoverageStrip({
  serviceSlug = "web-design",
  contextLabel,
}: Props) {
  const resolved = normalizeServiceSlug(serviceSlug);
  const subcopy = contextLabel
    ? `We deliver ${contextLabel} work for clients across Colorado's Front Range and beyond — from Fort Collins down to Pueblo and out to the Western Slope.`
    : "We serve clients across Colorado's Front Range and beyond — from Fort Collins down to Pueblo and out to the Western Slope.";

  return (
    <section className="border-y border-border bg-section-alt py-14 lg:py-20">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
            Service areas
          </p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Serving businesses across Colorado
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {subcopy}
          </p>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <CityChips label="Major metros" items={MAJOR_METROS} serviceSlug={resolved} />
          <CityChips label="Growth markets" items={GROWTH_MARKETS} serviceSlug={resolved} />
        </div>
      </div>
    </section>
  );
}
